import { useRouteRefParams } from '@backstage/core-plugin-api';
import {
  FavoriteEntity,
  entityRouteRef,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';
import { EntityHeaderBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { ownerRefs } from '../home/ownership';
import { getSourceUrl } from './entityMeta';
import { EntityPageHeaderView } from './EntityPageHeaderView';
import { useShareTitle } from '../../lib/shareTitle';

// Re-export the presentational view + pure helpers so importers/tests can reach
// them without pulling the Backstage entity context into the Vite bundle.
export * from './EntityPageHeaderView';

/**
 * Regask entity header — reads the entity from context (like the stock
 * `EntityHeader`) and feeds the presentational view. While the entity is still
 * loading it falls back to the kind/name from the route so the title doesn't
 * pop in.
 */
export const EntityPageHeader = () => {
  const { entity } = useAsyncEntity();
  const { kind: routeKind, name: routeName } =
    useRouteRefParams(entityRouteRef);

  const kind = entity?.kind ?? routeKind;
  const title = entity?.metadata.title ?? entity?.metadata.name ?? routeName;
  useShareTitle(`${title} - ${kind}`);

  const type =
    typeof entity?.spec?.type === 'string' ? entity.spec.type : undefined;
  const lifecycle =
    typeof entity?.spec?.lifecycle === 'string'
      ? entity.spec.lifecycle
      : undefined;

  return (
    <EntityPageHeaderView
      title={title}
      kind={kind}
      type={type}
      description={entity?.metadata.description}
      ownerRefs={entity ? ownerRefs(entity) : []}
      lifecycle={lifecycle}
      tags={entity?.metadata.tags}
      sourceUrl={entity ? getSourceUrl(entity) : undefined}
      favorite={entity ? <FavoriteEntity entity={entity} /> : undefined}
    />
  );
};

/**
 * Registers the Regask header for the catalog entity page. No filter, so it
 * replaces the stock `EntityHeader` for every entity kind (Component, User,
 * Group, API, System, Resource…). The grouped tabs are rendered separately by
 * `EntityTabs`, so this only swaps the title bar above them.
 */
export const entityPageHeader = EntityHeaderBlueprint.make({
  name: 'regask',
  params: {
    loader: async () => <EntityPageHeader />,
  },
});
